/**
 * @fileoverview Loading Spinner Component
 * 
 * Provides a full-screen modal loading spinner with multiple animation styles.
 * Uses react-spinners library and custom Xanec logo animation.
 * 
 * @module components/Spinner
 */

import {
  BarLoader,
  BeatLoader,
  CircleLoader,
  ClimbingBoxLoader,
  ClipLoader,
  ClockLoader,
  DotLoader,
  FadeLoader,
  GridLoader,
  HashLoader,
  MoonLoader,
  PacmanLoader,
  PuffLoader,
  PulseLoader,
  RingLoader,
  RiseLoader,
  RotateLoader,
  ScaleLoader,
  SkewLoader,
  SquareLoader,
  SyncLoader,
} from 'react-spinners';

import { SvgComponent } from './svg';
import xtlogo from './xanectech-animated-logo.svg';

import { Dialog, DialogOverlay, DialogContent } from './dialog';

import { cn } from '../lib/utils';
import { DialogTitle } from '@radix-ui/react-dialog';

/**
 * Props for the Spinner component
 * 
 * @interface SpinnerProps
 */
export interface SpinnerProps {
  /** Name of the spinner style to use (from spinnerList) */
  spinner?: string;
  /** If true, randomly selects a spinner style */
  random?: boolean;
  /** Color of the spinner animation */
  color: string;
  /** Whether the spinner is currently loading/visible */
  loading: boolean;
  /** Optional size and dimension configurations */
  options?: {
    /** Size of the spinner in pixels */
    size?: number;
    /** Height of the spinner in pixels */
    height?: number;
    /** Width of the spinner in pixels */
    width?: number;
    /** Radius for circular spinners */
    radius?: number;
    /** Margin around spinner elements */
    margin?: number;
  };
  /** Custom CSS properties override */
  cssOverride?: React.CSSProperties;
  /** Additional CSS classes */
  className?: string;
}

/**
 * List of available spinner animation names
 * 
 * @constant
 */
export const spinnerList = [
  'Bar',
  'Beat',
  'Circle',
  'Climbing',
  'Clip',
  'Clock',
  'Dot',
  'Fade',
  'Grid',
  'Hash',
  'Moon',
  'Pacman',
  'Puff',
  'Pulse',
  'Ring',
  'Rise',
  'Rotate',
  'Scale',
  'Skew',
  'Square',
  'Sync ',
  'Xanec',
];

/**
 * Mapping of spinner names to their react-spinners components
 * 
 * @constant
 */
export const spinnerComponents = [
  { name: 'Bar', component: BarLoader },
  { name: 'Beat', component: BeatLoader },
  { name: 'Circle', component: CircleLoader },
  { name: 'Climbing', component: ClimbingBoxLoader },
  { name: 'Clip', component: ClipLoader },
  { name: 'Clock', component: ClockLoader },
  { name: 'Dot', component: DotLoader },
  { name: 'Fade', component: FadeLoader },
  { name: 'Grid', component: GridLoader },
  { name: 'Hash', component: HashLoader },
  { name: 'Moon', component: MoonLoader },
  { name: 'Pacman', component: PacmanLoader },
  { name: 'Puff', component: PuffLoader },
  { name: 'Pulse', component: PulseLoader },
  { name: 'Ring', component: RingLoader },
  { name: 'Rise', component: RiseLoader },
  { name: 'Rotate', component: RotateLoader },
  { name: 'Scale', component: ScaleLoader },
  { name: 'Skew', component: SkewLoader },
  { name: 'Square', component: SquareLoader },
  { name: 'Sync', component: SyncLoader },
  {
    name: 'Xanec',
    component: () => (
      <SvgComponent file={xtlogo} className='h-[50px] w-[50px]' />
    ),
  },
];

/**
 * Full-screen modal loading spinner component
 * 
 * Displays a loading animation in a modal dialog overlay with customizable spinner styles.
 * Supports multiple animation types from react-spinners library plus a custom Xanec logo animation.
 * 
 * @param {SpinnerProps} props - Component props
 * @returns {JSX.Element} Modal dialog with animated spinner
 * 
 * @example
 * ```tsx
 * // Basic usage with default Xanec logo
 * <Spinner color="#3B82F6" loading={isLoading} />
 * 
 * // With specific spinner style
 * <Spinner 
 *   spinner="Circle" 
 *   color="#10B981" 
 *   loading={isLoading}
 *   options={{ size: 50 }}
 * />
 * 
 * // Random spinner selection
 * <Spinner 
 *   random={true} 
 *   color="#EF4444" 
 *   loading={isLoading}
 * />
 * ```
 */
function Spinner({
  spinner = 'Xanec',
  random = false,
  color,
  loading,
  options = {
    size: 20,
  },
  cssOverride,
  className,
}: SpinnerProps) {
  const cleanSpinner = (name: string) => name.trim();
  let spinnerName = cleanSpinner(spinner);

  if (random) {
    const randomIndex = Math.floor(Math.random() * spinnerList.length);
    const randomSpinner = spinnerList[randomIndex];
    spinnerName = randomSpinner ? cleanSpinner(randomSpinner) : spinnerName;
  }

  if (spinner) {
    spinnerName = cleanSpinner(spinner);
  }

  const spinnerEntry = spinnerComponents.find(
    (entry) => cleanSpinner(entry.name) === spinnerName,
  );

  if (spinnerEntry) {
    const SpinnerComponent = spinnerEntry.component;
    return (
      <Dialog open={loading} modal>
        <DialogOverlay className='flex items-center justify-center bg-black/20 dark:bg-black/40'>
          <DialogTitle className='sr-only'>Loading</DialogTitle>
          <DialogContent
            hideCloseButton={true}
            className={cn(
              'flex items-center justify-start h-25 max-w-[180px] text-sm',
              className,
            )}
          >
            <SpinnerComponent
              color={color}
              loading={loading}
              cssOverride={cssOverride}
              size={options.size ? options.size : undefined}
              height={options.height ? options.height : undefined}
              width={options.width ? options.width : undefined}
              radius={options.radius ? options.radius : undefined}
              margin={options.margin ? options.margin : undefined}
              className={className}
              aria-label='Loading'
              data-testid='spinner'
            />
            <p className='text-blue-600 font-medium'>
              Loading
              <span className="after:animate-dotLoop after:content-['']"></span>
            </p>
          </DialogContent>
        </DialogOverlay>
      </Dialog>
    );
  } else {
    return <ClimbingBoxLoader color={color} loading={loading} />;
  }
}

export { Spinner, xtlogo };
